'use client';

import * as React from 'react';
import { usePathname } from 'next/navigation';
import { useMarkAnnouncementsRead } from '@/api/mutations/announcements.mutations';
import { useUnreadAnnouncements } from '@/api/queries/announcements.queries';
import {
  isUnreadAnnouncement,
  newestPublishedAt,
  selectPopup,
  type PopupSelection,
} from '@/shared/lib/announcements/announcement-rules';
import { useTourActive } from '@/shared/lib/tours/tour-store';
import { useAuthStore } from '@/shared/stores/auth-store';
import type { Announcement } from '@/types';
import {
  getWhatsNewSlot,
  isPlaybackRoute,
  setWhatsNewOpen,
  setWhatsNewSlot,
} from '../prompt-coordination';
import { WHATS_NEW_HREF } from './constants';
import { decideWhatsNewSlot } from './decide-whats-new-slot';
import { reportWhatsNewError } from './report-whats-new-error';
import { useWhatsNewEnabled } from './use-whats-new-enabled';

const WhatsNewDialog = React.lazy(() => import('./whats-new-dialog'));

/** Longest the other prompts wait on a slow flag or fetch. */
const SLOT_TIMEOUT_MS = 5000;

type ShownSelection = Exclude<PopupSelection<Announcement>, { kind: 'none' }>;

/**
 * Session-start What's New popup. Fetches the unread announcements once per
 * session, claims the shared prompt slot when there is something to show
 * (so the app badge and location prompt wait), and lazy-mounts the dialog.
 * Renders nothing on the server and on the first client render — the query
 * only starts after hydration.
 */
export function WhatsNewHost() {
  const pathname = usePathname();
  const { eligibleUser, decided, enabled } = useWhatsNewEnabled();
  const { data, error, isSuccess } = useUnreadAnnouncements(enabled);
  const readAt = useAuthStore((s) => s.user?.last_announcements_read_at ?? null);
  const { mutate: markRead } = useMarkAnnouncementsRead();
  const [selection, setSelection] = React.useState<ShownSelection | null>(null);
  const [timedOut, setTimedOut] = React.useState(false);
  const tourActive = useTourActive();

  const unread = React.useMemo(
    () => (data?.announcements ?? []).filter((a) => isUnreadAnnouncement(a.published_at, readAt)),
    [data, readAt]
  );

  React.useEffect(() => {
    if (!eligibleUser) return;
    const timer = window.setTimeout(() => setTimedOut(true), SLOT_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [eligibleUser]);

  React.useEffect(() => {
    if (error) reportWhatsNewError(error, 'fetch');
  }, [error]);

  const decision = decideWhatsNewSlot({
    decided,
    enabled,
    loaded: isSuccess,
    failed: error !== null,
    unreadCount: unread.length,
    // A running tour holds the screen; the popup waits for it like for playback.
    suppressed: pathname === null || isPlaybackRoute(pathname) || tourActive,
    onHistoryPage: pathname === WHATS_NEW_HREF,
    timedOut,
  });

  React.useEffect(() => {
    if (!eligibleUser) return;
    if (decision !== 'claim') {
      setWhatsNewSlot(decision);
      return;
    }
    const slot = getWhatsNewSlot();
    if (slot === 'claimed' || slot === 'released') return;
    const next = selectPopup(unread);
    if (next.kind === 'none') return;
    setWhatsNewSlot('claimed');
    setWhatsNewOpen(true);
    setSelection(next);
  }, [decision, eligibleUser, unread]);

  const handleMarkRead = React.useCallback(() => {
    if (!selection) return;
    const shown = selection.kind === 'single' ? [selection.item] : selection.items;
    markRead(newestPublishedAt(shown), {
      onError: (err) => reportWhatsNewError(err, 'mark-read'),
    });
  }, [markRead, selection]);

  const handleClosed = React.useCallback(() => {
    setWhatsNewOpen(false);
    setSelection(null);
  }, []);

  if (!selection) return null;

  return (
    <React.Suspense fallback={null}>
      <WhatsNewDialog
        selection={selection}
        pathname={pathname}
        onMarkRead={handleMarkRead}
        onClosed={handleClosed}
      />
    </React.Suspense>
  );
}
